import { useMemo, useState } from "react";

import { Building2, Pencil, Plus } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useCreateSupplierCompanyProfile,
  useSupplierCompanyProfiles,
  useUpdateSupplierCompanyProfile,
} from "~/hooks/purchasing";
import { toast } from "~/hooks/use-toast";

import { SupplierCompanyProfileDialog } from "./SupplierCompanyProfileDialog";

import type {
  SupplierCompanyProfile,
  SupplierCompanyProfileFormData,
} from "~/types/purchasing";

interface SupplierCompanyProfilesCardProps {
  supplierId: string;
}

export function SupplierCompanyProfilesCard({
  supplierId,
}: SupplierCompanyProfilesCardProps) {
  const { data: profiles = [], isLoading } =
    useSupplierCompanyProfiles(supplierId);
  const createMutation = useCreateSupplierCompanyProfile();
  const updateMutation = useUpdateSupplierCompanyProfile();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SupplierCompanyProfile | undefined>();

  const takenCompanyIds = useMemo(
    () => profiles.map((p) => p.company_id),
    [profiles]
  );

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const openEdit = (profile: SupplierCompanyProfile) => {
    setEditing(profile);
    setDialogOpen(true);
  };

  const handleSubmit = (data: SupplierCompanyProfileFormData) => {
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, data },
        {
          onSuccess: () => {
            toast({
              title: "Empresa actualizada",
              description: "Se guardaron las cuentas de la empresa.",
            });
            setDialogOpen(false);
          },
          onError: (error: Error) =>
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            }),
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          toast({
            title: "Empresa asignada",
            description: "El proveedor quedó asociado a la empresa.",
          });
          setDialogOpen(false);
        },
        onError: (error: Error) =>
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          }),
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Cuentas por empresa</CardTitle>
        <Button size="sm" className="h-9" onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          Asignar empresa
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Sin empresas asignadas"
            description="Asigne este proveedor a una empresa para contabilizar sus facturas."
            action={
              <Button size="sm" className="h-9" onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" />
                Asignar empresa
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Cuenta por pagar</TableHead>
                <TableHead>Cuenta de gasto</TableHead>
                <TableHead>Condición de pago</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-gray-800">
                    {p.company?.name ?? "-"}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {p.ap_account_code || "-"}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {p.default_expense_account
                      ? `${p.default_expense_account.code} · ${p.default_expense_account.name}`
                      : "-"}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {p.payment_condition || "-"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={
                        p.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }
                    >
                      {p.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEdit(p)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <SupplierCompanyProfileDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        supplierId={supplierId}
        profile={editing}
        takenCompanyIds={takenCompanyIds}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    </Card>
  );
}
